import React, { Component } from 'react'
import { Descriptions, Form, FormInstance, Select, Tag } from 'antd'
import { AgeConsumer, AgeContextData } from '../../../../context/AgeContext'
import { ShelterConsumer, ShelterContextData } from 'context/ShelterContext'
import { DEFAULT_SELECT_FILTER_OPTION } from 'helpers/form-fields.helper'
import MappingStepper, { Step } from '../MappingStepper'
import { DIRECTION_LABEL, Flow, Partner } from '../shared/types'
import { resolveFlow } from '../shared/resolvers'
import { ActiveField, IncomingStep, ManualExternalInputs, PartnerField, ScopeField } from '../shared/fields'

type MappingAgeFormProps = {
  form: FormInstance
  partnerList: Partner[] | null
  /** Save-in-progress flag, forwarded to the final-step Save button. */
  loading?: boolean
}

type MappingAgeFormInnerProps = MappingAgeFormProps & {
  ageContext: AgeContextData
  shelterContext: ShelterContextData
}

type MappingAgeFormInnerState = {
  flow: Flow
}

class MappingAgeFormInner extends Component<MappingAgeFormInnerProps, MappingAgeFormInnerState> {
  constructor(props: MappingAgeFormInnerProps) {
    super(props)
    this.state = {
      flow: resolveFlow(props.form, 'age_key'),
    }
  }

  componentDidMount() {
    const { ageContext, shelterContext, form } = this.props

    // Initial values may settle after the Form mounts — re-resolve the flow.
    const flow = resolveFlow(form, 'age_key')
    if (flow !== this.state.flow) {
      this.setState({ flow })
    }

    if (!ageContext.fetching && !ageContext.ages?.length) {
      ageContext.requestAgeList({
        options: {
          limit: 9999
        }
      })
    }

    if (!shelterContext.fetching && !shelterContext.shelters?.length) {
      shelterContext.requestShelterList({
        options: {
          limit: 9999,
          sort: ['name']
        },
        search: {
          _fields: ["uuid", "name"]
        }
      })
    }
  }

  /** Resolve a shelter name for display, from the loaded shelter list. */
  shelterName = (uuid?: string) =>
    this.props.shelterContext.shelters?.find(s => s.uuid === uuid)?.name

  /** Resolve a partner display name. */
  partnerName = (key?: string) =>
    this.props.partnerList?.find(p => p.key === key)?.name || key

  /** Resolve an age name for display, from the loaded age list. */
  ageName = (key?: string) =>
    this.props.ageContext.ages?.find(a => a.key === key)?.name

  // ----- Field groups -----

  renderAgeField = () => {
    const { ageContext } = this.props
    const agesOptions = ageContext.ages?.map(age => ({
      label: age.name,
      value: age.key,
    }))

    return (
      <Form.Item
        label="Age"
        name="age_key"
        rules={[{ required: true, message: 'Missing an age' }]}
      >
        <Select
          showSearch
          filterOption={DEFAULT_SELECT_FILTER_OPTION}
          loading={ageContext.fetching}
          placeholder="Select an age"
          options={agesOptions}
        />
      </Form.Item>
    )
  }

  /** External target. Age has no external list to pick from, so it's a plain name + id. */
  renderExternalTarget = () => (
    <ManualExternalInputs />
  )

  /** Inbound only: read-only display of the incoming external value. */
  renderIncoming = () => (
    <IncomingStep
      form={this.props.form}
      partnerList={this.props.partnerList}
      externalRowLabel="External Age"
      hiddenFields={['partner_key', 'name', 'external_id']}
    />
  )

  // ----- Review -----

  renderReview = () => {
    const { form } = this.props

    const partnerKey = form.getFieldValue('partner_key')
    const shelterUuid = form.getFieldValue('shelter_uuid')
    const ageKey = form.getFieldValue('age_key')
    const name = form.getFieldValue('name')
    const externalId = form.getFieldValue('external_id')

    const ourSide = this.ageName(ageKey) || '—'
    const externalSide = [name, externalId].filter(Boolean).join(' · ') || '—'
    const mappingSummary = `${ourSide} ↔ ${externalSide}`

    return (
      <>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Direction">
            {DIRECTION_LABEL.BOTH}
          </Descriptions.Item>
          <Descriptions.Item label="Partner">{this.partnerName(partnerKey) || '—'}</Descriptions.Item>
          <Descriptions.Item label="Mapping">{mappingSummary}</Descriptions.Item>
          <Descriptions.Item label="Applies to">
            {shelterUuid ? (this.shelterName(shelterUuid) || shelterUuid) : <Tag>GLOBAL</Tag>}
          </Descriptions.Item>
        </Descriptions>

        <ActiveField />
      </>
    )
  }

  // ----- Step definitions -----

  steps = (): Step[] => {
    const { form, shelterContext } = this.props

    const reviewStep: Step = {
      key: 'review',
      title: 'Review & Save',
      fields: [],
      content: this.renderReview,
    }

    if (this.state.flow === 'inbound') {
      return [
        {
          key: 'incoming',
          title: 'Incoming',
          fields: [],
          content: this.renderIncoming,
        },
        {
          key: 'our-side',
          title: 'Our side',
          fields: ['age_key', 'shelter_uuid'],
          content: () => (
            <>
              {this.renderAgeField()}
              <ScopeField form={form} shelterContext={shelterContext} />
            </>
          ),
        },
        reviewStep,
      ]
    }

    return [
      {
        key: 'our-side',
        title: 'Our side',
        fields: ['age_key', 'partner_key', 'shelter_uuid'],
        content: () => (
          <>
            {this.renderAgeField()}
            <PartnerField partnerList={this.props.partnerList} />
            <ScopeField form={form} shelterContext={shelterContext} />
          </>
        ),
      },
      {
        key: 'external',
        title: 'External target',
        fields: ['name', 'external_id'],
        content: this.renderExternalTarget,
      },
      reviewStep,
    ]
  }

  render() {
    return <MappingStepper form={this.props.form} steps={this.steps()} loading={this.props.loading} />
  }
}

export default class MappingAgeForm extends Component<MappingAgeFormProps> {
  render() {
    return (
      <AgeConsumer>
        {ageContext =>
          <ShelterConsumer>
            {shelterContext => (
              <MappingAgeFormInner
                form={this.props.form}
                partnerList={this.props.partnerList}
                loading={this.props.loading}
                ageContext={ageContext}
                shelterContext={shelterContext}
              />
            )}
          </ShelterConsumer>}
      </AgeConsumer>
    )
  }
}
